import { ClinicalTaskEditFields } from '@bariendo/lib/clinical-tasks';
import { getOperationOutcomeMessage } from '@bariendo/lib/error';
import { Box, Text } from '@mantine/core';
import { createReference, formatHumanName } from '@medplum/core';
import { Patient, Task } from '@medplum/fhirtypes';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import PatientCardSelector from '@/components/PatientCardSelector';
import { useCreateTask } from '@/hooks/useCreateTask';
import { TaskForm } from '@/pages/PatientTimelinePage/TasksManagement/TaskForm';
import { alert } from '@/services/alert';

export interface InboxCreateTaskFormProps {
  onSave: (task: Task) => void;
  onCancel: () => void;
}

/**
 * Create a clinical task from the inbox.
 *
 * Deliberately the SAME TaskForm the patient timeline uses — not a lookalike —
 * so title, assignee, due date, priority, description, validation and the
 * Escape / Cmd+Enter shortcuts stay identical on both surfaces by construction.
 *
 * The one field this surface adds is the patient. On the timeline the patient
 * is implied by where you are standing; here nothing implies it, so it is
 * asked for first and Save stays disabled until it is answered. Picking the
 * wrong patient writes a task onto the wrong chart, so the picker shows date
 * of birth and journey stage alongside the name rather than the name alone.
 */
export function InboxCreateTaskForm({ onSave, onCancel }: InboxCreateTaskFormProps): ReactElement {
  const [patient, setPatient] = useState<Patient | undefined>(undefined);
  const patientRef = useMemo(() => (patient ? createReference(patient) : undefined), [patient]);
  const { createTask, loading } = useCreateTask(patientRef);

  const handleSubmit = useCallback(
    async (fields: ClinicalTaskEditFields) => {
      if (!patient) {
        return;
      }
      try {
        const task = await createTask(fields);
        // Name the patient back: unlike the timeline, the inbox gives no
        // standing context for which chart this landed on.
        alert.success({
          title: 'Task created',
          message: [task.description, formatHumanName(patient.name?.[0])].filter(Boolean).join(' · '),
        });
        onSave(task);
      } catch (e) {
        alert.error(`Couldn't create task: ${getOperationOutcomeMessage(e)}`);
      }
    },
    [createTask, onSave, patient]
  );

  return (
    <TaskForm
      loading={loading}
      submitLabel="Save"
      submitDisabled={!patient}
      onCancel={onCancel}
      onSubmit={handleSubmit}
      headerSlot={
        <Box>
          <Text size="xs" fw={500} mb={4}>
            Patient
          </Text>
          <PatientCardSelector
            patient={patient}
            setSelectedPatient={setPatient}
            disabled={loading}
            placeholder="Search patients by name"
            usePatientCard={false}
          />
        </Box>
      }
    />
  );
}
